import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ImageUpload } from '@/components/ImageUpload';
import { ArrowLeft, Save, Trash2, Lock, Eye, Ticket, Crown, Instagram, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useUser } from '@/lib/auth';
import { getEventImage } from '@/lib/eventHelpers';
import { generateInstagramGraphic } from '@/lib/igGraphicGenerator';
import { logError } from '@/lib/logger';

const EMPTY = {
  titolo: '', titolo_en: '', data: '', orario: '21:00', locale_id: '', zona: '',
  descrizione: '', descrizione_en: '', locandina: '', link_acquisto: '',
  tavolo_prive: false, link_tavolo: '', stato_revisione: 'In Revisione',
  in_evidenza: false, artisti: '', genere_musicale: '', prezzo_ingresso: '',
};

export default function AdminEventoForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useUser();
  const canPublish = !!(user?.is_super_admin || user?.permissions?.can_publish_events);
  const [form, setForm] = useState(EMPTY);
  const [zones, setZones] = useState([]);
  const [locali, setLocali] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(!!id);
  const [generatingIG, setGeneratingIG] = useState(false);

  useEffect(() => {
    api.get('/admin/zones').then((r) => setZones(r.data?.zones || []));
    api.get('/admin/locali').then((r) => setLocali(r.data || []));
    if (id) {
      api.get(`/admin/eventi/${id}`).then((r) => setForm({ ...EMPTY, ...r.data })).catch(() => toast.error('Evento non trovato')).finally(() => setLoading(false));
    }
  }, [id]);

  // Auto-fill zona from locale
  useEffect(() => {
    if (form.locale_id && !form.zona) {
      const l = locali.find((x) => x.id === form.locale_id);
      if (l) setForm((f) => ({ ...f, zona: l.zona }));
    }
  }, [form.locale_id, locali]);

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.titolo || !form.data || !form.orario || !form.locale_id || !form.zona) {
      toast.error('Compila titolo, data, orario, locale e zona'); return;
    }
    setBusy(true);
    try {
      if (id) {
        await api.patch(`/admin/eventi/${id}`, form);
        toast.success('Evento aggiornato');
      } else {
        const r = await api.post('/admin/eventi', form);
        toast.success('Evento creato');
        navigate(`/admin/eventi/${r.data.id}/modifica`, { replace: true });
      }
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore'); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!id) return;
    if (!window.confirm('Eliminare l\'evento?')) return;
    await api.delete(`/admin/eventi/${id}`);
    toast.success('Eliminato'); navigate('/admin/eventi');
  };

  const generateIG = async () => {
    if (generatingIG) return;
    const locale = locali.find((l) => l.id === form.locale_id);
    const imageUrl = getEventImage(form, locale);
    if (!imageUrl) {
      toast.error("Aggiungi un'immagine all'evento o al locale prima di generare la locandina");
      return;
    }
    if (!form.titolo || !form.data) {
      toast.error('Servono almeno titolo e data per generare la locandina');
      return;
    }
    setGeneratingIG(true);
    try {
      await generateInstagramGraphic({
        titolo: form.titolo,
        data: form.data,
        orario: form.orario,
        venueName: locale?.nome || '',
        imageUrl,
      });
      toast.success('Locandina IG generata e scaricata');
    } catch (err) {
      logError('IG generation failed', err);
      const msg = err?.response?.data?.detail || err?.message || 'Errore nella generazione della locandina';
      toast.error(typeof msg === 'string' ? msg : 'Errore nella generazione della locandina');
    } finally {
      setGeneratingIG(false);
    }
  };

  if (loading) return <AdminLayout title="Caricamento…"><div className="py-12 text-center text-[color:var(--text-muted)]">Caricamento…</div></AdminLayout>;

  return (
    <AdminLayout title={id ? 'Modifica Evento' : 'Nuovo Evento'}>
      <div className="flex items-center gap-2 mb-4">
        <Link to="/admin/eventi"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-1"/>Eventi</Button></Link>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-4">
          <Section title="Informazioni base">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Titolo evento *"><Input data-testid="evento-form-titolo" value={form.titolo} onChange={(e) => onChange('titolo', e.target.value)} className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
              <Field label="Titolo (EN)"><Input value={form.titolo_en || ''} onChange={(e) => onChange('titolo_en', e.target.value)} className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
              <Field label="Data *"><Input data-testid="evento-form-data" type="date" value={form.data} onChange={(e) => onChange('data', e.target.value)} className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
              <Field label="Orario inizio *"><Input data-testid="evento-form-orario" type="time" value={form.orario} onChange={(e) => onChange('orario', e.target.value)} className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
              <Field label="Locale *">
                <Select value={form.locale_id} onValueChange={(v) => onChange('locale_id', v)}>
                  <SelectTrigger data-testid="evento-form-locale" className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"><SelectValue placeholder="Seleziona locale"/></SelectTrigger>
                  <SelectContent className="bg-[color:var(--surface)] border-[color:var(--border-c)] max-h-[280px]">
                    {locali.map((l) => <SelectItem key={l.id} value={l.id}>{l.nome} • {l.zona}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Zona *">
                <Select value={form.zona} onValueChange={(v) => onChange('zona', v)}>
                  <SelectTrigger data-testid="evento-form-zona" className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"><SelectValue placeholder="Seleziona zona"/></SelectTrigger>
                  <SelectContent className="bg-[color:var(--surface)] border-[color:var(--border-c)]">
                    {zones.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </Section>

          <Section title="Descrizione SEO">
            <Field label="Descrizione (IT) — Rich text"><Textarea data-testid="evento-form-descrizione" rows={6} value={form.descrizione || ''} onChange={(e) => onChange('descrizione', e.target.value)} className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
            <Field label="Descrizione (EN)"><Textarea rows={4} value={form.descrizione_en || ''} onChange={(e) => onChange('descrizione_en', e.target.value)} className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
          </Section>

          <Section title="Dettagli serata">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Artisti / DJ"><Input value={form.artisti || ''} onChange={(e) => onChange('artisti', e.target.value)} className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
              <Field label="Genere musicale"><Input value={form.genere_musicale || ''} onChange={(e) => onChange('genere_musicale', e.target.value)} placeholder="House, Hip-hop, Reggaeton…" className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
              <Field label="Prezzo ingresso"><Input value={form.prezzo_ingresso || ''} onChange={(e) => onChange('prezzo_ingresso', e.target.value)} placeholder="€ 20" className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
            </div>
          </Section>

          <Section title="Link & Booking">
            <Field label="Link Acquisto Biglietto (opzionale)"><Input data-testid="evento-form-link-acquisto" value={form.link_acquisto || ''} onChange={(e) => onChange('link_acquisto', e.target.value)} placeholder="https://…" className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></Field>
            <div className="flex items-center justify-between rounded-lg border border-[color:var(--border-c)] bg-[color:var(--surface-2)] p-3">
              <div>
                <Label className="font-medium">Tavolo Privé attivo</Label>
                <p className="text-xs text-[color:var(--text-muted)]">Se attivo, il pulsante PRENOTA TAVOLO sarà visibile (richiede link).</p>
              </div>
              <Switch data-testid="evento-form-tavolo-prive" checked={form.tavolo_prive} onCheckedChange={(v) => onChange('tavolo_prive', v)} />
            </div>
            <Field label="Link Tavolo (opzionale)"><Input data-testid="evento-form-link-tavolo" value={form.link_tavolo || ''} onChange={(e) => onChange('link_tavolo', e.target.value)} placeholder="https://…" disabled={!form.tavolo_prive} className="bg-[color:var(--surface-2)] border-[color:var(--border-c)] disabled:opacity-50"/></Field>
          </Section>
        </div>

        <div className="space-y-4">
          <Section title="Stato & Visibilità">
            {canPublish ? (
              <Field label="Stato Revisione">
                <Select value={form.stato_revisione} onValueChange={(v) => onChange('stato_revisione', v)}>
                  <SelectTrigger data-testid="evento-form-stato" className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"><SelectValue/></SelectTrigger>
                  <SelectContent className="bg-[color:var(--surface)] border-[color:var(--border-c)]">
                    <SelectItem value="In Revisione">In Revisione</SelectItem>
                    <SelectItem value="Pubblicato">Pubblicato</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            ) : (
              <div className="rounded-lg border border-[color:var(--border-c)] bg-[color:var(--surface-2)] p-3 flex items-center gap-2 text-xs text-[color:var(--text-muted)]" data-testid="evento-form-stato-locked"><Lock className="w-3.5 h-3.5 text-amber-400"/>L'evento sarà salvato in <b className="text-[color:var(--gold)]">In Revisione</b>. Non hai il permesso di pubblicare.</div>
            )}
            {canPublish ? (
              <div className="flex items-center justify-between rounded-lg border border-[color:var(--border-c)] bg-[color:var(--surface-2)] p-3">
                <Label className="font-medium">In Evidenza (Featured)</Label>
                <Switch data-testid="evento-form-in-evidenza" checked={form.in_evidenza} onCheckedChange={(v) => onChange('in_evidenza', v)} />
              </div>
            ) : null}
          </Section>
          <Section title="Locandina">
            <ImageUpload label="Immagine evento" testId="evento-form-locandina" value={form.locandina || ''} onChange={(v) => onChange('locandina', v)} />
            <p className="text-xs text-[color:var(--text-subtle)] mt-1">Se vuoto, verrà usato automaticamente il logo del locale.</p>
            {id && (
              <Button
                type="button"
                data-testid="evento-form-generate-ig"
                onClick={generateIG}
                disabled={generatingIG}
                variant="outline"
                className="w-full mt-3 border-[color:var(--gold)] text-[color:var(--gold)] hover:bg-[rgba(214,185,140,0.08)] disabled:opacity-60"
              >
                {generatingIG ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Generazione…</>
                ) : (
                  <><Instagram className="w-4 h-4 mr-2"/>Genera Locandina IG</>
                )}
              </Button>
            )}
          </Section>
          {id && (
            <Section title="Statistiche">
              <div className="grid grid-cols-3 gap-2" data-testid="evento-form-stats">
                <Stat icon={Eye} label="Views" value={form.view_count || 0} />
                <Stat icon={Ticket} label="Acquista" value={form.click_buy_count || 0} color="text-emerald-400" />
                <Stat icon={Crown} label="Tavolo" value={form.click_table_count || 0} color="text-amber-400" />
              </div>
              <p className="text-xs text-[color:var(--text-subtle)] mt-2">Conteggi aggiornati in tempo reale. Le views sono uniche per sessione utente.</p>
            </Section>
          )}
          <div className="flex flex-col gap-2">
            <Button data-testid="evento-form-save" onClick={save} disabled={busy} className="bg-[color:var(--gold-strong)] hover:bg-[#C6A21A] text-black font-semibold"><Save className="w-4 h-4 mr-2"/>{busy ? 'Salvataggio…' : 'Salva evento'}</Button>
            {id && <Button variant="outline" onClick={remove} className="border-red-700 text-red-300 hover:bg-red-900/20"><Trash2 className="w-4 h-4 mr-2"/>Elimina</Button>}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-xl border border-[color:var(--border-c)] bg-[color:var(--surface)] p-4 space-y-3">
      <h3 className="font-heading text-lg text-[color:var(--gold)] mb-2">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (<div><Label className="text-xs uppercase tracking-wider text-[color:var(--text-subtle)] mb-1 block">{label}</Label>{children}</div>);
}

function Stat({ icon: Icon, label, value, color = 'text-[color:var(--gold)]' }) {
  return (
    <div className="rounded-lg border border-[color:var(--border-c)] bg-[color:var(--surface-2)] p-3 text-center">
      <Icon className={`w-4 h-4 mx-auto ${color} mb-1`} />
      <div className="text-2xl font-heading">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-subtle)] mt-0.5">{label}</div>
    </div>
  );
}
